import React, { useState, useEffect, useCallback, useRef } from 'react';
import { adminOpsService, maintenanceService } from '../services';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Shield, RefreshCw, Database, HardDrive, ArrowDownToLine, Clock, CheckCircle2,
  XCircle, AlertTriangle, Loader2, ChevronRight, GitBranch, Settings,
  History, Wrench, Server, Users, Mail, Timer
} from 'lucide-react';

// Status badge
function StatusBadge({ status }) {
  const variants = {
    complete: { variant: 'default', className: 'bg-green-600', icon: CheckCircle2 },
    failed: { variant: 'destructive', className: '', icon: XCircle },
    started: { variant: 'secondary', className: 'bg-blue-500 text-white', icon: Loader2 },
    processing: { variant: 'secondary', className: 'bg-blue-500 text-white', icon: Loader2 },
    partial: { variant: 'secondary', className: 'bg-amber-500 text-white', icon: AlertTriangle },
    dry_run: { variant: 'outline', className: '', icon: Settings },
    recovery_branch_created: { variant: 'secondary', className: 'bg-emerald-600 text-white', icon: GitBranch },
  };
  const cfg = variants[status] || { variant: 'outline', className: '', icon: Clock };
  const Icon = cfg.icon;
  return (
    <Badge variant={cfg.variant} className={`text-[10px] gap-1 ${cfg.className}`}>
      <Icon className={`h-3 w-3 ${status === 'started' || status === 'processing' ? 'animate-spin' : ''}`} />
      {status}
    </Badge>
  );
}

// Time formatting
function RelativeTime({ iso }) {
  if (!iso) return <span className="text-muted-foreground">—</span>;
  const d = new Date(iso);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  let text;
  if (diff < 60) text = 'just now';
  else if (diff < 3600) text = `${Math.floor(diff / 60)}m ago`;
  else if (diff < 86400) text = `${Math.floor(diff / 3600)}h ago`;
  else text = d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  return <span className="text-sm" title={d.toISOString()}>{text}</span>;
}

// Format bytes
function formatBytes(b) {
  if (!b) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(b) / Math.log(k));
  return parseFloat((b / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// Config check card
function ConfigCard({ label, configured, detail }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${configured ? 'border-green-200 bg-green-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex items-center gap-2">
        {configured ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <AlertTriangle className="h-4 w-4 text-amber-600" />}
        <span className="text-sm font-medium">{label}</span>
      </div>
      <span className="text-xs text-muted-foreground">{detail || (configured ? 'Ready' : 'Not configured')}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════════

export default function MaintenancePage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('overview');
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncConfirm, setSyncConfirm] = useState('');
  const [syncing, setSyncing] = useState(false);

  // DB info
  const [dbInfo, setDbInfo] = useState(null);
  const [dbHistory, setDbHistory] = useState([]);

  // R2 info
  const [r2Info, setR2Info] = useState(null);
  const [r2Snapshots, setR2Snapshots] = useState([]);
  const [snapshotting, setSnapshotting] = useState(false);

  // Restore state
  const [branches, setBranches] = useState([]);
  const [restoreBranch, setRestoreBranch] = useState('');
  const [restoreTimestamp, setRestoreTimestamp] = useState('');
  const [restoreConfirm, setRestoreConfirm] = useState('');
  const [useRecoveryBranch, setUseRecoveryBranch] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);

  // R2 restore
  const [r2RestoreSnapshot, setR2RestoreSnapshot] = useState('');
  const [r2RestoreMode, setR2RestoreMode] = useState('full');
  const [r2Preview, setR2Preview] = useState(null);
  const [r2RestoreConfirm, setR2RestoreConfirm] = useState('');
  const [r2Restoring, setR2Restoring] = useState(false);

  // Existing maintenance mode
  const [maintenanceStatus, setMaintenanceStatus] = useState(null);
  const [maintenanceMessage, setMaintenanceMessage] = useState('');
  const [maintenanceConfirmOpen, setMaintenanceConfirmOpen] = useState(false);
  const [activeSessions, setActiveSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [countdownRemaining, setCountdownRemaining] = useState(null);
  const countdownRef = useRef(null);
  const [emailSettings, setEmailSettings] = useState({ send_recovery_email: true, recovery_email_internal: true, recovery_email_external: true });
  const [activating, setActivating] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, hist] = await Promise.all([
        adminOpsService.configStatus(),
        adminOpsService.history(null, 50),
      ]);
      setConfig(cfg);
      setHistory(hist);
    } catch (err) {
      console.error('Failed to load config:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Load tab-specific data ──
  const loadSyncData = useCallback(async () => {
    try { setSyncStatus(await adminOpsService.syncStatus()); } catch (e) { console.error(e); }
  }, []);

  const loadDbData = useCallback(async () => {
    try {
      const [info, hist] = await Promise.all([adminOpsService.dbInfo(), adminOpsService.dbHistory()]);
      setDbInfo(info);
      setDbHistory(hist);
    } catch (e) { console.error(e); }
  }, []);

  const loadR2Data = useCallback(async () => {
    try {
      const [info, snaps] = await Promise.all([adminOpsService.r2Info(), adminOpsService.r2Snapshots()]);
      setR2Info(info);
      setR2Snapshots(snaps);
    } catch (e) { console.error(e); }
  }, []);

  const loadBranches = useCallback(async () => {
    try { setBranches(await adminOpsService.neonBranches()); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (activeTab === 'sync') loadSyncData();
    else if (activeTab === 'backups') { loadDbData(); loadR2Data(); }
    else if (activeTab === 'restore') { loadBranches(); loadR2Data(); }
  }, [activeTab, loadSyncData, loadDbData, loadR2Data, loadBranches]);

  // ── Handlers ──
  const handleRefreshStaging = async () => {
    setSyncing(true);
    try {
      await adminOpsService.refreshStaging(syncConfirm);
      toast({ title: 'Staging Refreshed', description: 'Staging database has been reset from production.' });
      setSyncConfirm('');
      loadSyncData();
      loadAll();
    } catch (err) {
      toast({ title: 'Sync Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSyncing(false);
    }
  };

  const [snapshotTaskId, setSnapshotTaskId] = useState(null);
  const [snapshotProgress, setSnapshotProgress] = useState(null);
  const snapshotPollRef = useRef(null);

  // Cleanup snapshot polling on unmount
  useEffect(() => {
    return () => {
      if (snapshotPollRef.current) clearInterval(snapshotPollRef.current);
    };
  }, []);

  const handleCreateSnapshot = async () => {
    setSnapshotting(true);
    setSnapshotProgress(null);
    try {
      const result = await adminOpsService.createR2Snapshot();
      if (result.task_id) {
        // Background task started — poll for progress
        setSnapshotTaskId(result.task_id);
        toast({ title: 'Snapshot Started', description: 'Running in background...' });
        let errorCount = 0;
        snapshotPollRef.current = setInterval(async () => {
          try {
            const status = await adminOpsService.r2SnapshotStatus(result.task_id);
            setSnapshotProgress(status);
            if (status.status === 'complete' || status.status === 'partial') {
              clearInterval(snapshotPollRef.current);
              snapshotPollRef.current = null;
              setSnapshotting(false);
              setSnapshotTaskId(null);
              toast({ title: 'Snapshot Complete', description: `${status.objects_copied}/${status.total_objects} objects copied.` });
              loadR2Data();
              loadAll();
            } else if (status.status === 'failed') {
              clearInterval(snapshotPollRef.current);
              snapshotPollRef.current = null;
              setSnapshotting(false);
              setSnapshotTaskId(null);
              toast({ title: 'Snapshot Failed', description: status.error || 'Unknown error', variant: 'destructive' });
            }
            errorCount = 0;
          } catch {
            errorCount++;
            if (errorCount >= 5) {
              clearInterval(snapshotPollRef.current);
              snapshotPollRef.current = null;
              setSnapshotting(false);
              setSnapshotTaskId(null);
              toast({ title: 'Snapshot Status Unknown', description: 'Could not poll for status after 5 retries.', variant: 'destructive' });
            }
          }
        }, 3000);
      } else {
        // Synchronous completion (shouldn't happen with new backend, but handle gracefully)
        toast({ title: 'Snapshot Created', description: `${result.objects_copied || 0} objects copied.` });
        loadR2Data();
        loadAll();
        setSnapshotting(false);
      }
    } catch (err) {
      toast({ title: 'Snapshot Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
      setSnapshotting(false);
    }
  };

  const handleDbRestore = async () => {
    setRestoring(true);
    setRestoreResult(null);
    try {
      const result = await adminOpsService.restoreDb(restoreBranch, restoreTimestamp, restoreConfirm, useRecoveryBranch);
      setRestoreResult(result);
      toast({ title: 'Restore Initiated', description: result.message });
      setRestoreConfirm('');
      loadAll();
    } catch (err) {
      toast({ title: 'Restore Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setRestoring(false);
    }
  };

  const handleR2DryRun = async () => {
    try {
      const result = await adminOpsService.restoreR2(r2RestoreSnapshot, r2RestoreMode, true);
      setR2Preview(result);
    } catch (err) {
      toast({ title: 'Preview Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const handleR2Restore = async () => {
    setR2Restoring(true);
    try {
      const result = await adminOpsService.restoreR2(r2RestoreSnapshot, r2RestoreMode, false, r2RestoreConfirm);
      toast({ title: 'Restore Complete', description: `${result.objects_restored} objects restored.` });
      setR2Preview(null);
      setR2RestoreConfirm('');
      loadAll();
    } catch (err) {
      toast({ title: 'Restore Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setR2Restoring(false);
    }
  };

  const handleToggleMaintenance = async () => {
    try {
      if (maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active) {
        // Deactivate
        setDeactivating(true);
        const result = await maintenanceService.deactivate();
        toast({ title: 'Maintenance Disabled', description: `${result.users_logged_out || 0} users were logged out. ${result.recovery_emails_queued || 0} recovery emails queued.` });
      } else {
        // Activate with countdown
        setActivating(true);
        await maintenanceService.activate(20, maintenanceMessage || undefined);
        toast({ title: 'Maintenance Countdown Started', description: '20-second countdown initiated. Users will be logged out automatically.' });
      }
      setMaintenanceConfirmOpen(false);
      await loadMaintenanceStatus();
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || err.message || 'Failed to toggle maintenance mode', variant: 'destructive' });
    } finally {
      setActivating(false);
      setDeactivating(false);
    }
  };

  const loadMaintenanceStatus = useCallback(async () => {
    try {
      const status = await maintenanceService.getStatus();
      setMaintenanceStatus(status);
      if (status.message && !maintenanceMessage) {
        setMaintenanceMessage(status.message);
      }
    } catch {
      // Maintenance endpoint might not be accessible
    }
  }, []);

  const loadActiveSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const result = await maintenanceService.getSessions();
      setActiveSessions(result.sessions || []);
    } catch {
      setActiveSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, []);

  const loadEmailSettings = useCallback(async () => {
    try {
      const result = await maintenanceService.getEmailSettings();
      setEmailSettings(result);
    } catch {
      // Use defaults
    }
  }, []);

  const handleEmailSettingsChange = async (key, value) => {
    const updated = { ...emailSettings, [key]: value };
    setEmailSettings(updated);
    try {
      await maintenanceService.updateEmailSettings(updated);
    } catch {
      toast({ title: 'Error', description: 'Failed to update email settings', variant: 'destructive' });
    }
  };

  // Load maintenance status and sessions on mount and when tab switches
  useEffect(() => {
    loadMaintenanceStatus();
  }, [loadMaintenanceStatus]);

  useEffect(() => {
    if (activeTab === 'maintenance') {
      loadActiveSessions();
      loadEmailSettings();
    }
  }, [activeTab, loadActiveSessions, loadEmailSettings]);

  // Poll maintenance status during countdown
  useEffect(() => {
    if (maintenanceStatus?.is_countdown_active) {
      setCountdownRemaining(maintenanceStatus.countdown_seconds_remaining);
      countdownRef.current = setInterval(async () => {
        try {
          const status = await maintenanceService.getStatus();
          setMaintenanceStatus(status);
          if (status.is_countdown_active) {
            setCountdownRemaining(status.countdown_seconds_remaining);
          } else {
            setCountdownRemaining(null);
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            if (status.is_active) {
              toast({ title: 'Maintenance Active', description: `${status.users_logged_out || 0} non-admin users logged out.` });
              loadActiveSessions();
            }
          }
        } catch {
          // ignore poll errors
        }
      }, 1000);
    } else {
      setCountdownRemaining(null);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [maintenanceStatus?.is_countdown_active]);

  const featureEnabled = config?.feature_enabled;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-6" data-testid="maintenance-page">
      <div className="flex items-center gap-3">
        <Shield className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-xl font-semibold">Backup, Recovery & Maintenance</h1>
          <p className="text-sm text-muted-foreground">Database sync, backups, restore, and system controls</p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className={`grid w-full ${featureEnabled ? 'grid-cols-5' : 'grid-cols-2'}`} data-testid="ops-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Server className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />Overview
          </TabsTrigger>
          {featureEnabled && (
            <TabsTrigger value="sync" data-testid="tab-sync">
              <RefreshCw className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />Env Sync
            </TabsTrigger>
          )}
          {featureEnabled && (
            <TabsTrigger value="backups" data-testid="tab-backups">
              <Database className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />Backups
            </TabsTrigger>
          )}
          {featureEnabled && (
            <TabsTrigger value="restore" data-testid="tab-restore">
              <ArrowDownToLine className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />Restore
            </TabsTrigger>
          )}
          <TabsTrigger value="maintenance" data-testid="tab-maintenance">
            <Wrench className="h-3.5 w-3.5 mr-1.5 hidden sm:inline" />System
          </TabsTrigger>
        </TabsList>

        {/* ══════════ OVERVIEW ══════════ */}
        <TabsContent value="overview" className="space-y-4">
          {!featureEnabled && (
            <Card className="border-blue-200 bg-blue-50" data-testid="feature-flag-disabled-banner">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-blue-900">Backup & Recovery Console is disabled</p>
                    <p className="text-xs text-blue-700 mt-1">
                      The Env Sync, Backups, and Restore tabs are hidden. To enable, set <code className="bg-blue-100 px-1 rounded">ENABLE_BACKUP_CONSOLE=true</code> in your environment variables and restart.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Integration Status</CardTitle>
              <CardDescription>Configuration state of backup and recovery services</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              <ConfigCard label="Neon API" configured={config?.neon?.configured} detail={config?.neon?.project_id ? `Project: ${config.neon.project_id}` : null} />
              <ConfigCard label="Production Branch" configured={!!config?.neon?.production_branch_id} detail={config?.neon?.production_branch_id} />
              <ConfigCard label="Staging Branch" configured={!!config?.neon?.staging_branch_id} detail={config?.neon?.staging_branch_id} />
              <ConfigCard label="Backup R2 Bucket" configured={config?.backup_r2?.configured} detail={config?.backup_r2?.bucket} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Settings className="h-4 w-4" /> Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                <div><span className="text-muted-foreground">Retention:</span> <strong>{config?.settings?.retention_days} days</strong></div>
                <div><span className="text-muted-foreground">Schedule:</span> <strong>{config?.settings?.schedule_hour}:00 UTC</strong></div>
                <div><span className="text-muted-foreground">Mask on sync:</span> <strong>{config?.settings?.mask_on_sync ? 'Yes' : 'No'}</strong></div>
                <div><span className="text-muted-foreground">Recovery prefix:</span> <strong>{config?.settings?.recovery_branch_prefix}</strong></div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Recent Operations</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {history.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No operations recorded yet</p>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-muted/30 text-xs">
                    <TableHead>Operation</TableHead><TableHead>Target</TableHead><TableHead>Status</TableHead>
                    <TableHead>By</TableHead><TableHead>When</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {history.slice(0, 15).map(op => (
                      <TableRow key={op.id}>
                        <TableCell className="text-sm font-medium">{op.operation_type}</TableCell>
                        <TableCell className="text-sm text-muted-foreground font-mono text-xs">{op.target}</TableCell>
                        <TableCell><StatusBadge status={op.status} /></TableCell>
                        <TableCell className="text-sm text-muted-foreground">{op.initiated_by?.split('@')[0]}</TableCell>
                        <TableCell><RelativeTime iso={op.started_at} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════ ENVIRONMENT SYNC ══════════ */}
        <TabsContent value="sync" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Refresh Staging from Production</CardTitle>
              <CardDescription>Reset the staging Neon branch to match current production data</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!config?.neon?.configured ? (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />
                  Neon API is not configured. Set <code className="bg-amber-100 px-1 rounded">NEON_API_KEY</code>, <code className="bg-amber-100 px-1 rounded">NEON_PROJECT_ID</code>, and <code className="bg-amber-100 px-1 rounded">NEON_STAGING_BRANCH_ID</code> to enable environment sync.
                </div>
              ) : (
                <>
                  {syncStatus?.staging_branch && !syncStatus?.staging_branch?.error && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Branch:</span> <strong>{syncStatus.staging_branch.name}</strong></div>
                      <div><span className="text-muted-foreground">State:</span> <strong>{syncStatus.staging_branch.current_state}</strong></div>
                      <div><span className="text-muted-foreground">Updated:</span> <RelativeTime iso={syncStatus.staging_branch.updated_at} /></div>
                    </div>
                  )}
                  <Separator />
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-3">
                    <p className="text-sm text-red-800 font-medium">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      This will replace ALL staging data with a copy of production. This action cannot be undone.
                    </p>
                    <div className="flex items-end gap-3">
                      <div className="flex-1">
                        <Label className="text-xs text-red-700">Type <strong>REFRESH STAGING</strong> to confirm</Label>
                        <Input
                          value={syncConfirm}
                          onChange={(e) => setSyncConfirm(e.target.value)}
                          placeholder="REFRESH STAGING"
                          className="mt-1 border-red-300"
                          data-testid="sync-confirm-input"
                        />
                      </div>
                      <Button
                        variant="destructive"
                        disabled={syncConfirm !== 'REFRESH STAGING' || syncing}
                        onClick={handleRefreshStaging}
                        data-testid="sync-execute-btn"
                      >
                        {syncing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                        Execute Sync
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Sync History */}
          {syncStatus?.history?.length > 0 && (
            <Card>
              <CardHeader className="pb-3"><CardTitle className="text-base">Sync History</CardTitle></CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader><TableRow className="bg-muted/30 text-xs">
                    <TableHead>Status</TableHead><TableHead>By</TableHead><TableHead>Started</TableHead><TableHead>Completed</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {syncStatus.history.map(op => (
                      <TableRow key={op.id}>
                        <TableCell><StatusBadge status={op.status} /></TableCell>
                        <TableCell className="text-sm">{op.initiated_by?.split('@')[0]}</TableCell>
                        <TableCell><RelativeTime iso={op.started_at} /></TableCell>
                        <TableCell><RelativeTime iso={op.completed_at} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ══════════ BACKUPS ══════════ */}
        <TabsContent value="backups" className="space-y-4">
          {/* Database */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> Database (Neon)</CardTitle>
              <CardDescription>Point-in-time recovery via Neon's built-in restore window</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {!dbInfo?.configured && !dbInfo?.error ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />Neon API not configured.
                </div>
              ) : dbInfo?.error ? (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">{dbInfo.error}</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                    <div><span className="text-muted-foreground">Project:</span> <strong>{dbInfo?.project_name}</strong></div>
                    <div><span className="text-muted-foreground">Region:</span> <strong>{dbInfo?.region}</strong></div>
                    <div><span className="text-muted-foreground">Branches:</span> <strong>{dbInfo?.branch_count}</strong></div>
                    <div><span className="text-muted-foreground">Restore window:</span> <strong>{dbInfo?.history_retention_seconds ? `${Math.round(dbInfo.history_retention_seconds / 3600)}h` : '—'}</strong></div>
                  </div>
                  {dbInfo?.branches?.length > 0 && (
                    <>
                      <Separator />
                      <Table>
                        <TableHeader><TableRow className="bg-muted/30 text-xs">
                          <TableHead>Branch</TableHead><TableHead>ID</TableHead><TableHead>Parent</TableHead><TableHead>State</TableHead><TableHead>Updated</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {dbInfo.branches.map(b => (
                            <TableRow key={b.id}>
                              <TableCell className="text-sm font-medium">{b.name}</TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">{b.id?.slice(0, 16)}...</TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">{b.parent_id ? `${b.parent_id.slice(0, 12)}...` : '(root)'}</TableCell>
                              <TableCell><Badge variant="outline" className="text-[10px]">{b.current_state}</Badge></TableCell>
                              <TableCell><RelativeTime iso={b.updated_at} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* R2 Objects */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><HardDrive className="h-4 w-4" /> Report Objects (R2)</CardTitle>
                  <CardDescription>Snapshot report files to a dedicated backup bucket</CardDescription>
                </div>
                {config?.backup_r2?.configured && (
                  <Button size="sm" onClick={handleCreateSnapshot} disabled={snapshotting} data-testid="create-snapshot-btn">
                    {snapshotting ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <HardDrive className="h-3.5 w-3.5 mr-1.5" />}
                    Create Snapshot
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {!config?.backup_r2?.configured ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />Backup R2 not configured. Set <code className="bg-amber-100 px-1 rounded">BACKUP_R2_*</code> env vars.
                  {config?.backup_r2?.var_check && (
                    <div className="mt-2 text-xs space-y-0.5">
                      {Object.entries(config.backup_r2.var_check).map(([k, v]) => (
                        <div key={k}>{v ? '✓' : '✗'} {k.replace(/_/g, ' ')}</div>
                      ))}
                    </div>
                  )}
                  {config?.backup_r2?.init_error && (
                    <div className="mt-2 text-xs text-red-700">Error: {config.backup_r2.init_error}</div>
                  )}
                </div>
              ) : (
                <>
                  {/* Snapshot progress bar */}
                  {snapshotting && snapshotProgress && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm space-y-2" data-testid="snapshot-progress">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                        <span className="font-medium text-blue-800">Snapshot in progress...</span>
                      </div>
                      <div className="w-full bg-blue-100 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                          style={{ width: `${snapshotProgress.total_objects ? Math.round((snapshotProgress.objects_copied / snapshotProgress.total_objects) * 100) : 0}%` }}
                        />
                      </div>
                      <div className="text-xs text-blue-700">
                        {snapshotProgress.objects_copied}/{snapshotProgress.total_objects} objects • {formatBytes(snapshotProgress.total_size_bytes || 0)}
                      </div>
                    </div>
                  )}
                  {r2Info?.source_stats && (
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div><span className="text-muted-foreground">Source bucket:</span> <strong>{r2Info.source_stats.bucket}</strong></div>
                      <div><span className="text-muted-foreground">Objects:</span> <strong>{r2Info.source_stats.object_count}</strong></div>
                      <div><span className="text-muted-foreground">Total size:</span> <strong>{formatBytes(r2Info.source_stats.total_size_bytes)}</strong></div>
                    </div>
                  )}
                  {r2Snapshots.length > 0 && (
                    <>
                      <Separator />
                      <p className="text-xs font-medium text-muted-foreground">Snapshot History</p>
                      <Table>
                        <TableHeader><TableRow className="bg-muted/30 text-xs">
                          <TableHead>Snapshot ID</TableHead><TableHead>Objects</TableHead><TableHead>Size</TableHead><TableHead>By</TableHead><TableHead>When</TableHead>
                        </TableRow></TableHeader>
                        <TableBody>
                          {r2Snapshots.map(s => (
                            <TableRow key={s.snapshot_id}>
                              <TableCell className="text-xs font-mono">{s.snapshot_id}</TableCell>
                              <TableCell className="text-sm">{s.object_count}</TableCell>
                              <TableCell className="text-sm">{formatBytes(s.total_size_bytes)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground">{s.initiated_by?.split('@')[0]}</TableCell>
                              <TableCell><RelativeTime iso={s.timestamp} /></TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════ RESTORE ══════════ */}
        <TabsContent value="restore" className="space-y-4">
          {/* Database Restore */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Database className="h-4 w-4" /> Database Restore</CardTitle>
              <CardDescription>Restore a Neon branch to a specific point in time. Default: creates a recovery branch for validation first.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!config?.neon?.configured ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />Neon API not configured.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Target Branch</Label>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
                        value={restoreBranch}
                        onChange={(e) => setRestoreBranch(e.target.value)}
                        data-testid="restore-branch-select"
                      >
                        <option value="">Select branch...</option>
                        {branches.map(b => (
                          <option key={b.id} value={b.id}>
                            {b.name} {b.id === config?.neon?.production_branch_id ? '(PRODUCTION)' : b.id === config?.neon?.staging_branch_id ? '(STAGING)' : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Restore to timestamp (ISO 8601)</Label>
                      <Input
                        type="datetime-local"
                        value={restoreTimestamp}
                        onChange={(e) => setRestoreTimestamp(e.target.value)}
                        className="mt-1"
                        data-testid="restore-timestamp-input"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch checked={useRecoveryBranch} onCheckedChange={setUseRecoveryBranch} data-testid="use-recovery-branch" />
                    <Label className="text-sm">Create recovery branch first (recommended)</Label>
                  </div>
                  {restoreBranch === config?.neon?.production_branch_id && !useRecoveryBranch && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                      <AlertTriangle className="h-4 w-4 inline mr-1" />
                      <strong>Direct production restore without recovery branch is extremely destructive.</strong>
                    </div>
                  )}
                  <Separator />
                  <div className="flex items-end gap-3">
                    <div className="flex-1">
                      <Label className="text-xs">
                        Type <strong>{restoreBranch === config?.neon?.production_branch_id ? 'RESTORE PRODUCTION' : 'RESTORE DATABASE'}</strong> to confirm
                      </Label>
                      <Input
                        value={restoreConfirm}
                        onChange={(e) => setRestoreConfirm(e.target.value)}
                        placeholder={restoreBranch === config?.neon?.production_branch_id ? 'RESTORE PRODUCTION' : 'RESTORE DATABASE'}
                        className="mt-1"
                        data-testid="restore-confirm-input"
                      />
                    </div>
                    <Button
                      variant="destructive"
                      disabled={
                        !restoreBranch || !restoreTimestamp || restoring ||
                        (restoreBranch === config?.neon?.production_branch_id
                          ? restoreConfirm !== 'RESTORE PRODUCTION'
                          : restoreConfirm !== 'RESTORE DATABASE')
                      }
                      onClick={handleDbRestore}
                      data-testid="restore-execute-btn"
                    >
                      {restoring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowDownToLine className="h-4 w-4 mr-1.5" />}
                      Restore
                    </Button>
                  </div>
                  {restoreResult && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded text-sm space-y-1">
                      <p className="font-medium text-green-800"><CheckCircle2 className="h-4 w-4 inline mr-1" />{restoreResult.message}</p>
                      {restoreResult.recovery_branch_name && (
                        <p className="text-green-700">Recovery branch: <code className="bg-green-100 px-1 rounded">{restoreResult.recovery_branch_name}</code></p>
                      )}
                      {restoreResult.connection_host && (
                        <p className="text-green-700">Host: <code className="bg-green-100 px-1 rounded">{restoreResult.connection_host}</code></p>
                      )}
                      {restoreResult.recovery_branch_id && (
                        <Button size="sm" variant="outline" className="mt-2" onClick={async () => {
                          try {
                            await adminOpsService.deleteRecoveryBranch(restoreResult.recovery_branch_id);
                            toast({ title: 'Branch Deleted' });
                            setRestoreResult(null);
                          } catch (err) {
                            toast({ title: 'Delete Failed', description: err.message, variant: 'destructive' });
                          }
                        }}>
                          Delete Recovery Branch
                        </Button>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* R2 Restore */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><HardDrive className="h-4 w-4" /> R2 Object Restore</CardTitle>
              <CardDescription>Restore report files from a snapshot. Dry-run preview is required before any restore.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!config?.backup_r2?.configured ? (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
                  <AlertTriangle className="h-4 w-4 inline mr-2" />Backup R2 not configured.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs">Select Snapshot</Label>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
                        value={r2RestoreSnapshot}
                        onChange={(e) => { setR2RestoreSnapshot(e.target.value); setR2Preview(null); }}
                        data-testid="r2-snapshot-select"
                      >
                        <option value="">Select snapshot...</option>
                        {r2Snapshots.map(s => (
                          <option key={s.snapshot_id} value={s.snapshot_id}>
                            {s.snapshot_id} ({s.object_count} objects, {formatBytes(s.total_size_bytes)})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Restore Mode</Label>
                      <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm mt-1"
                        value={r2RestoreMode}
                        onChange={(e) => { setR2RestoreMode(e.target.value); setR2Preview(null); }}
                        data-testid="r2-restore-mode"
                      >
                        <option value="full">Full Snapshot</option>
                        <option value="prefix">By Prefix</option>
                        <option value="object">Individual Objects</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" disabled={!r2RestoreSnapshot} onClick={handleR2DryRun} data-testid="r2-dryrun-btn">
                      Preview (Dry Run)
                    </Button>
                  </div>
                  {r2Preview && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded text-sm space-y-2">
                      <p className="font-medium text-blue-800">Dry Run Preview</p>
                      <div className="grid grid-cols-2 gap-2 text-blue-700">
                        <div>Objects to restore: <strong>{r2Preview.objects_to_restore}</strong></div>
                        <div>Total size: <strong>{formatBytes(r2Preview.total_size_bytes)}</strong></div>
                      </div>
                      {r2Preview.preview?.length > 0 && (
                        <details className="text-xs">
                          <summary className="cursor-pointer text-blue-600">View objects ({r2Preview.preview.length})</summary>
                          <div className="mt-1 max-h-32 overflow-y-auto bg-blue-100/50 rounded p-2 font-mono">
                            {r2Preview.preview.map((k, i) => <div key={i}>{k}</div>)}
                          </div>
                        </details>
                      )}
                      <Separator className="bg-blue-200" />
                      <div className="flex items-end gap-3">
                        <div className="flex-1">
                          <Label className="text-xs text-blue-700">Type <strong>RESTORE OBJECTS</strong> to confirm</Label>
                          <Input
                            value={r2RestoreConfirm}
                            onChange={(e) => setR2RestoreConfirm(e.target.value)}
                            placeholder="RESTORE OBJECTS"
                            className="mt-1 border-blue-300"
                            data-testid="r2-restore-confirm"
                          />
                        </div>
                        <Button
                          variant="destructive"
                          disabled={r2RestoreConfirm !== 'RESTORE OBJECTS' || r2Restoring}
                          onClick={handleR2Restore}
                          data-testid="r2-restore-execute-btn"
                        >
                          {r2Restoring ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <ArrowDownToLine className="h-4 w-4 mr-1.5" />}
                          Execute Restore
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ══════════ SYSTEM / MAINTENANCE ══════════ */}
        <TabsContent value="maintenance" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Wrench className="h-4 w-4" /> Maintenance Mode</CardTitle>
              <CardDescription>Put the application into maintenance mode. Non-admin users will be logged out after a 20-second countdown.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Status indicator */}
              <div className="flex items-center justify-between p-4 border rounded-lg">
                <div className="flex items-center gap-3">
                  <div className={`h-3 w-3 rounded-full ${
                    maintenanceStatus?.is_countdown_active ? 'bg-orange-500 animate-pulse' :
                    maintenanceStatus?.is_active ? 'bg-amber-500 animate-pulse' : 'bg-green-500'
                  }`} />
                  <div>
                    <p className="text-sm font-medium" data-testid="maintenance-status-label">
                      {maintenanceStatus?.is_countdown_active ? 'Countdown in Progress' :
                       maintenanceStatus?.is_active ? 'Maintenance Mode Active' : 'System Online'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {maintenanceStatus?.is_countdown_active ? 'Users are being warned before logout' :
                       maintenanceStatus?.is_active
                        ? `${maintenanceStatus.users_logged_out || 0} users were logged out`
                        : 'All systems operational'}
                    </p>
                  </div>
                </div>
                <Button
                  variant={maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active ? 'default' : 'destructive'}
                  size="sm"
                  onClick={() => setMaintenanceConfirmOpen(true)}
                  disabled={activating || deactivating}
                  data-testid="maintenance-toggle-btn"
                >
                  {(activating || deactivating) && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
                  {maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active ? 'Disable' : 'Enable'} Maintenance
                </Button>
              </div>

              {/* Countdown timer */}
              {maintenanceStatus?.is_countdown_active && countdownRemaining !== null && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg" data-testid="countdown-display">
                  <div className="flex items-center gap-3">
                    <Timer className="h-5 w-5 text-orange-600" />
                    <div>
                      <p className="text-sm font-semibold text-orange-800">Countdown: {countdownRemaining}s remaining</p>
                      <p className="text-xs text-orange-600">Users will be automatically logged out when the countdown reaches 0</p>
                    </div>
                    <div className="ml-auto text-3xl font-bold text-orange-700 tabular-nums">{countdownRemaining}</div>
                  </div>
                  <div className="mt-2 w-full bg-orange-100 rounded-full h-2">
                    <div
                      className="bg-orange-500 h-2 rounded-full transition-all duration-1000"
                      style={{ width: `${Math.max(0, (countdownRemaining / 20) * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Logged-out users list */}
              {maintenanceStatus?.is_active && maintenanceStatus?.logged_out_users?.length > 0 && (
                <div className="space-y-2" data-testid="logged-out-users">
                  <p className="text-xs font-medium text-muted-foreground">Users Logged Out During This Maintenance</p>
                  <Table>
                    <TableHeader><TableRow className="bg-muted/30 text-xs">
                      <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Type</TableHead><TableHead>Roles</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {maintenanceStatus.logged_out_users.map((u, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-sm">{u.first_name} {u.last_name}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                          <TableCell><Badge variant="outline" className="text-[10px]">{u.user_type || 'INTERNAL'}</Badge></TableCell>
                          <TableCell className="text-xs">{(u.roles || []).join(', ')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Custom message (only when not active) */}
              {!maintenanceStatus?.is_active && !maintenanceStatus?.is_countdown_active && (
                <div>
                  <Label className="text-xs">Maintenance Message (shown to users during countdown)</Label>
                  <Input
                    value={maintenanceMessage}
                    onChange={(e) => setMaintenanceMessage(e.target.value)}
                    placeholder="System maintenance will begin shortly. Please save your work."
                    className="mt-1"
                    data-testid="maintenance-message-input"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Active Sessions */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Active Sessions</CardTitle>
                  <CardDescription>Users active in the last 30 minutes</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={loadActiveSessions} disabled={sessionsLoading} data-testid="refresh-sessions-btn">
                  {sessionsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {activeSessions.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No active sessions detected</p>
              ) : (
                <Table>
                  <TableHeader><TableRow className="bg-muted/30 text-xs">
                    <TableHead>User</TableHead><TableHead>Email</TableHead><TableHead>Roles</TableHead><TableHead>Last Activity</TableHead><TableHead>Page</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {activeSessions.map((s) => (
                      <TableRow key={s.user_id}>
                        <TableCell className="text-sm font-medium">{s.first_name} {s.last_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{s.email}</TableCell>
                        <TableCell>
                          {(s.roles || []).map(r => (
                            <Badge key={r} variant="outline" className="text-[10px] mr-1">{r}</Badge>
                          ))}
                        </TableCell>
                        <TableCell><RelativeTime iso={s.last_activity} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{s.current_page || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              <p className="text-xs text-muted-foreground mt-2">{activeSessions.length} active session{activeSessions.length !== 1 ? 's' : ''}</p>
            </CardContent>
          </Card>

          {/* Recovery Email Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Mail className="h-4 w-4" /> Recovery Email Settings</CardTitle>
              <CardDescription>Configure whether to send "system back online" emails when maintenance is disabled</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-sm">Send recovery emails on deactivation</Label>
                <Switch
                  checked={emailSettings.send_recovery_email}
                  onCheckedChange={(v) => handleEmailSettingsChange('send_recovery_email', v)}
                  data-testid="email-recovery-toggle"
                />
              </div>
              {emailSettings.send_recovery_email && (
                <>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Email internal users (staff/auditors)</Label>
                    <Switch
                      checked={emailSettings.recovery_email_internal}
                      onCheckedChange={(v) => handleEmailSettingsChange('recovery_email_internal', v)}
                      data-testid="email-internal-toggle"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label className="text-sm text-muted-foreground">Email external users (clients)</Label>
                    <Switch
                      checked={emailSettings.recovery_email_external}
                      onCheckedChange={(v) => handleEmailSettingsChange('recovery_email_external', v)}
                      data-testid="email-external-toggle"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Environment Info</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">App Env:</span> <strong>{process.env.REACT_APP_BACKEND_URL?.includes('preview') ? 'Staging' : 'Production'}</strong></div>
                <div><span className="text-muted-foreground">API URL:</span> <code className="text-xs bg-muted px-1 rounded">{process.env.REACT_APP_BACKEND_URL}</code></div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Maintenance confirm dialog */}
      <AlertDialog open={maintenanceConfirmOpen} onOpenChange={setMaintenanceConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active ? 'Disable' : 'Enable'} Maintenance Mode?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active
                ? 'This will restore normal access for all users. Recovery emails will be sent to users who were logged out (if enabled).'
                : 'A 20-second countdown will start. All non-admin users will see a warning and will be automatically logged out when the countdown ends.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleMaintenance} data-testid="maintenance-confirm-btn">
              {maintenanceStatus?.is_active || maintenanceStatus?.is_countdown_active ? 'Disable' : 'Enable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
